/**
 * ShareOnXButton
 *
 * Reusable button that generates a pre-written X/Twitter post
 * about a hospital's price transparency compliance status.
 * Can be used inline (no modal) for compact contexts.
 */

import { useState } from "react";
import { useBrand } from "@/config/brands";
import { Button } from "@/components/ui/button";
import { Copy, CheckCircle2, Twitter } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export interface ShareHospital {
  id: string;
  name: string;
  city: string;
  state: string;
  bed_count?: number | null;
  cpt_count?: number | null;
  drg_count?: number | null;
  has_uploaded_file?: boolean;
  total_procedures?: number | null;
}

interface ShareOnXButtonProps {
  hospital: ShareHospital;
  /** Show just the X icon (compact for table rows) */
  compact?: boolean;
}

function buildTweet(hospital: ShareHospital, brandDomain: string): string {
  const missing: string[] = [];
  if (hospital.has_uploaded_file && !hospital.drg_count) missing.push("DRG");
  if (hospital.has_uploaded_file && !hospital.cpt_count) missing.push("CPT");
  const noData = !hospital.has_uploaded_file || !hospital.total_procedures;

  const core = noData
    ? `${hospital.name} (${hospital.city}, ${hospital.state}) has not posted required hospital prices under federal law (45 CFR §180) — mandatory since Jan 2021.`
    : `${hospital.name} (${hospital.city}, ${hospital.state}) has incomplete price disclosure under federal law (45 CFR §180).${missing.length ? ` Missing: ${missing.join(", ")} codes.` : ""}`;

  const bedCount = hospital.bed_count;
  const fineAmt = bedCount
    ? `$${Math.min(bedCount * 10, 5500).toLocaleString()}`
    : "$300–$5,500";

  const suffix = `\n\nMax CMS fine: ${fineAmt}/day. Collected from this hospital: $0.\n\n→ ${brandDomain}/hospital/${hospital.id}\n\n#HealthcareTransparency #HospitalPrices #${hospital.state}`;
  const full = core + suffix;

  if (full.length <= 280) return full;
  const trimmed = core.slice(0, core.length - (full.length - 277)) + "…";
  return trimmed + suffix;
}

export function ShareOnXButton({ hospital, compact = false }: ShareOnXButtonProps) {
  const brand = useBrand();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const tweet = buildTweet(hospital, brand.primaryDomain);
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(tweet);
    setCopied(true);
    toast({ title: "Tweet copied to clipboard" });
    setTimeout(() => setCopied(false), 2000);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-muted transition-colors"
          title="Copy tweet"
        >
          {copied
            ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
            : <Copy className="w-3.5 h-3.5 text-muted-foreground" />
          }
        </button>
        <a
          href={tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="p-1 rounded hover:bg-muted transition-colors"
          title="Post on X"
        >
          <Twitter className="w-3.5 h-3.5 text-muted-foreground hover:text-black transition-colors" />
        </a>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={handleCopy}
      >
        {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied!" : "Copy Tweet"}
      </Button>
      <Button
        asChild
        size="sm"
        className="gap-2"
        style={{ backgroundColor: "#000", color: "#fff" }}
      >
        <a href={tweetUrl} target="_blank" rel="noopener noreferrer">
          <Twitter className="w-4 h-4" />
          Post on X
        </a>
      </Button>
    </div>
  );
}
